// src/features/previsionf/PrevisionFView.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Prévision par horizon 1→11 semaines
// Sélection : Famille / Sous-famille / Dépôt / Horizon
// Affichage : tableau détaillé + graphique barres + intervalles de confiance
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ErrorBar,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import { getMembers } from "../../cube/api";
import {
  getForecastDepots,
  getForecastDepotsFiltered,
  getForecastFamilles,
  getForecastHistorique,
  getForecastSousFamilles,
  getForecastVendeurs,
  postPrevisionHorizon,
} from "../api";
import { CUBE_NAME } from "../../cube/cubeSlice";
import { Spinner } from "../../../shared/ui/Spinner";
import { colors } from "../../../shared/ui/tokens";

// ── Types ─────────────────────────────────────────────────────────────────────
interface FamilleDto {
  codFamp: string;
  designation: string;
}
interface SousFamDto {
  codFam: string;
  desgFam: string;
  codFamp: string;
}
interface HistoriqueStep {
  date: string;
  ca_reel: number;
}
interface DepotDto {
  codDep: string;
  libelle: string;
}

interface PrevisionStep {
  step: number;
  date: string;
  ca_predit: number;
  borne_basse: number;
  borne_haute: number;
}

interface PrevisionFResponse {
  success: boolean;
  famille?: string;
  sous_famille?: string;
  depot?: string;
  horizon?: number;
  ca_total?: number;
  date_from?: string;
  date_to?: string;
  previsions?: PrevisionStep[];
  error?: string;
}

// ── Palette ───────────────────────────────────────────────────────────────────
const NW = {
  bg: colors.neutral[100],
  card: colors.neutral[0],
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.accent[300],
  violetLight: colors.brand[100],
  violetBorder: "rgba(29,91,168,0.16)",
  text: colors.neutral[900],
  textMuted: colors.brand[500],
  green: colors.accent[500],
  greenBg: colors.neutral[50],
  red: colors.danger[500],
  redBg: colors.neutral[100],
  orange: colors.warning[600],
  orangeBg: colors.neutral[50],
  border: "rgba(29,91,168,0.13)",
  shadow: "0 2px 20px rgba(29,91,168,0.09)",
};

const BAR_COLORS = [
  "#2B5CE6",
  "#8B5CF6",
  "#2B9EDF",
  "#3ECFBF",
  "#C084FC",
  "#2B5CE6",
  "#1D4ED8",
  "#5B21B6",
  "#1A3DA8",
  "#3B0764",
  "#2E1065",
];

const HORIZONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtCa = (v: number | null | undefined): string => {
  if (v == null || isNaN(v)) return "—";
  return (
    new Intl.NumberFormat("fr-TN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(v) + " DT"
  );
};

const fmtDate = (s: string): string => {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
};

// ── API (service de prévision, via api/endpoints) ─────────────────────────────
// ── Après la fonction apiDepots() existante, ajoute cette nouvelle fonction ──
async function apiDepotsFiltered(
  famille: string,
  sousFamille: string,
): Promise<DepotDto[]> {
  try {
    const r = await getForecastDepotsFiltered<DepotDto>(famille, sousFamille);
    return r.data ?? [];
  } catch (e) {
    console.error("apiDepotsFiltered error:", e);
    return [];
  }
}
// ── Nouvelle fonction API ─────────────────────────────────────────────────────
async function apiVendeurs(
  depot: string,
  famille: string,
  sousFamille: string,
): Promise<VendeurDto[]> {
  try {
    const r = await getForecastVendeurs<VendeurDto>(depot, famille, sousFamille);
    return r.data ?? [];
  } catch (e) {
    console.error("apiVendeurs error:", e);
    return [];
  }
}

async function apiHistorique(
  famille: string,
  sousFamille: string,
  depot: string,
  vendeur: string,   // ← nouveau paramètre
  n = 10,
): Promise<HistoriqueStep[]> {
  try {
    const r = await getForecastHistorique({ famille, sousFamille, depot, n, vendeur });
    return r.data ?? [];
  } catch (e) {
    console.error("apiHistorique error:", e);
    return [];
  }
}

async function apiFamilles(): Promise<FamilleDto[]> {
  try {
    const r = await getForecastFamilles<FamilleDto>();
    if (!r.success) {
      console.error("apiFamilles API error:", r.error);
      return [];
    }
    if (!r.data || r.data.length === 0) {
      console.warn("apiFamilles: liste vide reçue du serveur");
    }
    return r.data ?? [];
  } catch (e) {
    console.error("apiFamilles network error:", e);
    return [];
  }
}

async function apiSousFamilles(famille: string): Promise<SousFamDto[]> {
  try {
    const r = await getForecastSousFamilles<SousFamDto>(famille);
    return r.data ?? [];
  } catch (e) {
    console.error("apiSousFamilles error:", e);
    return [];
  }
}

async function apiDepots(): Promise<DepotDto[]> {
  try {
    const r = await getForecastDepots<DepotDto>();
    return r.data ?? [];
  } catch (e) {
    console.error("apiDepots error:", e);
    return [];
  }
}

async function apiPrevision(payload: {
  famille: string;
  sous_famille: string;
  depot: string;
  horizon: number;
  vendeur?: string;   // ← nouveau
}): Promise<PrevisionFResponse> {
  return postPrevisionHorizon<PrevisionFResponse>(payload);
}
const CustomTooltipCombined: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const isHist = d?.isHistorique;
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1.5px solid ${isHist ? "#00A08C33" : NW.violetBorder}`,
        borderRadius: 12,
        padding: "10px 14px",
        fontSize: 12,
        boxShadow: "0 4px 20px rgba(15,23,42,0.1)",
      }}
    >
      {isHist ? (
        <>
          <div style={{ fontWeight: 800, color: "#00A08C", marginBottom: 6 }}>
            📅 Semaine réelle — {fmtDate(d.date)}
          </div>
          <div style={{ color: "#00A08C", fontWeight: 700 }}>
            📊 CA Réel : {fmtCa(d.ca_reel)}
          </div>
        </>
      ) : (
        <>
          <div style={{ fontWeight: 800, color: NW.violet, marginBottom: 6 }}>
            Semaine {d.step} — {fmtDate(d.date)}
          </div>
          <div style={{ color: NW.violet, fontWeight: 700 }}>
            🔮 Prédit : {fmtCa(d.ca_predit)}
          </div>
          <div style={{ color: NW.textMuted, fontWeight: 600 }}>
            ▲ Haut : {fmtCa(d.borne_haute)}
          </div>
          <div style={{ color: NW.textMuted, fontWeight: 600 }}>
            ▼ Bas : {fmtCa(d.borne_basse)}
          </div>
        </>
      )}
    </div>
  );
};

// ── Tooltip personnalisé ──────────────────────────────────────────────────────
const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload as PrevisionStep & {
    errorBar?: [number, number];
  };
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1.5px solid ${NW.violetBorder}`,
        borderRadius: 12,
        padding: "10px 14px",
        fontSize: 12,
        boxShadow: "0 4px 20px rgba(29,91,168,0.15)",
      }}
    >
      <div style={{ fontWeight: 800, color: NW.violet, marginBottom: 6 }}>
        Semaine {d.step} — {fmtDate(d.date)}
      </div>
      <div style={{ color: NW.violet, fontWeight: 700 }}>
        🔮 Prédit : {fmtCa(d.ca_predit)}
      </div>
      <div style={{ color: NW.textMuted, fontWeight: 600 }}>
        ▲ Haut : {fmtCa(d.borne_haute)}
      </div>
      <div style={{ color: NW.textMuted, fontWeight: 600 }}>
        ▼ Bas : {fmtCa(d.borne_basse)}
      </div>
    </div>
  );
};

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================
interface PrevisionFViewProps {
  onSwitchToSousFamille?: () => void;
  onSwitchToFamille: () => void;
}

const PrevisionFView: React.FC<PrevisionFViewProps> = ({
  onSwitchToSousFamille = () => {},
  onSwitchToFamille = () => {},
}) => {
  // ── Données de référence ───────────────────────────────────────────────────
  const [familles, setFamilles] = useState<FamilleDto[]>([]);
  const [sousFamilles, setSousFamilles] = useState<SousFamDto[]>([]);
  const [vendeurs, setVendeurs] = useState<VendeurDto[]>([]);
  const [loadingVendeurs, setLoadingVendeurs] = useState(false);
  const [vendeur, setVendeur] = useState<string>("");
  const [depots, setDepots] = useState<DepotDto[]>([]);
  const [allDepots, setAllDepots] = useState<DepotDto[]>([]); // ← tous les dépôts (fallback)
  const [loadingDepots, setLoadingDepots] = useState(false);
  const [depotSearch, setDepotSearch] = useState(""); // ← recherche dépôt
  const [historique, setHistorique] = useState<HistoriqueStep[]>([]);

  // ── Sélections ─────────────────────────────────────────────────────────────
  const [famille, setFamille] = useState<string>("");
  const [sousFamille, setSousFamille] = useState<string>("");
  const [depot, setDepot] = useState<string>("");
  const [horizon, setHorizon] = useState<number>(4);

  // ── État prévision ─────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PrevisionFResponse | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // ── Chargement référentiels ────────────────────────────────────────────────
  // ── État erreur familles ───────────────────────────────────────────────────
const [famillesError, setFamillesError] = useState<string | null>(null);
const [loadingFamilles, setLoadingFamilles] = useState(true);

useEffect(() => {
  setLoadingFamilles(true);
  apiFamilles()
    .then((data) => {
      console.log("Familles reçues:", data);
      setFamilles(data);
      if (data.length === 0) {
        setFamillesError("Aucune famille disponible — vérifiez la connexion au serveur.");
      }
    })
    .catch(() => {
      setFamillesError("Impossible de charger les familles (erreur réseau).");
    })
    .finally(() => setLoadingFamilles(false));
}, []);

  // Charger TOUS les dépôts au montage (fallback si filtre échoue)
  useEffect(() => {
    apiDepots().then((data) => setAllDepots(data)).catch(() => {});
  }, []);
  // Charger les dépôts filtrés dès que sous-famille est choisie,
  // avec fallback sur tous les dépôts si le filtre échoue ou retourne vide
  useEffect(() => {
  if (!famille || !sousFamille) {
    setDepots([]);
    setDepot("");
    setDepotSearch("");
    setVendeurs([]);
    setVendeur("");
    return;
  }
  setLoadingDepots(true);
  apiDepotsFiltered(famille, sousFamille).then((data) => {
    if (data.length > 0) {
      setDepots(data);
    } else {
      // Fallback : utiliser tous les dépôts disponibles
      setDepots(allDepots);
    }
    setDepot("");
    setDepotSearch("");
    setVendeurs([]);
    setVendeur("");
    setLoadingDepots(false);
  }).catch(() => {
    setDepots(allDepots);
    setDepot("");
    setDepotSearch("");
    setLoadingDepots(false);
  });
}, [famille, sousFamille, allDepots]);

// ← Nouveau useEffect : charge les vendeurs dès que dépôt est choisi
useEffect(() => {
  if (!depot || !famille || !sousFamille) {
    setVendeurs([]);
    setVendeur("");
    return;
  }
  setLoadingVendeurs(true);
  apiVendeurs(depot, famille, sousFamille).then((data) => {
    setVendeurs(data);
    setVendeur("");
    setLoadingVendeurs(false);
  });
}, [depot, famille, sousFamille]);

  useEffect(() => {
    if (!famille) {
      setSousFamilles([]);
      setSousFamille("");
      return;
    }
    apiSousFamilles(famille).then((data) => {
      setSousFamilles(data);
      setSousFamille("");
    });
  }, [famille]);

  // ── Reset résultat si paramètres changent ──────────────────────────────────
  useEffect(() => {
    setResult(null);
    setErrMsg(null);
    setHistorique([]); // ← ajouter cette ligne
  }, [famille, sousFamille, depot, vendeur, horizon]);  // ← ajouter vendeur


  // ── Validation ─────────────────────────────────────────────────────────────
  const canForecast = !!famille && !!sousFamille && !!depot;
  const canAutoPreview = !!famille && !!sousFamille;

  // ── Prévision automatique dès que famille + sous-famille choisies ──────────
  // (utilise le 1er dépôt disponible ou lance sans dépôt pour preview rapide)
  const [autoResult, setAutoResult] = useState<PrevisionFResponse | null>(null);
  const [autoLoading, setAutoLoading] = useState(false);

  useEffect(() => {
    setAutoResult(null);
    if (!famille || !sousFamille) return;
    // Attend que les dépôts soient chargés, puis lance sur le 1er dépôt dispo
    if (loadingDepots) return;
    if (depots.length === 0) return;
    const premierDepot = depots[0].codDep;
    setAutoLoading(true);
    apiPrevision({
      famille,
      sous_famille: sousFamille,
      depot: premierDepot,
      horizon,
    })
      .then((res) => { if (res.success) setAutoResult(res); })
      .catch(() => {})
      .finally(() => setAutoLoading(false));
  }, [famille, sousFamille, depots, horizon]);

  // ── Lancer la prévision ────────────────────────────────────────────────────
  const handleForecast = useCallback(async () => {
    if (!canForecast) return;
    setLoading(true);
    setResult(null);
    setErrMsg(null);
    try {
      const res = await apiPrevision({
        famille,
        sous_famille: sousFamille,
        depot,
        horizon,
        vendeur: vendeur || undefined,
      });
      if (res.success) {
        setResult(res);
        const hist = await apiHistorique(famille, sousFamille, depot, vendeur, 10);
        setHistorique(hist);
      } else {
        setErrMsg(res.error ?? "Erreur inconnue");
      }
    } catch (e: any) {
      setErrMsg(e?.message ?? "Erreur réseau");
    } finally {
      setLoading(false);
    }
  }, [famille, sousFamille, depot, vendeur, horizon, canForecast]);

  // ── Données graphique — une seule valeur unifiée "ca_value" ───────────────
  const histChartData = historique.map((h, i) => ({
    name: `${fmtDate(h.date)}`,
    ca_value: h.ca_reel,
    errorBar: null as [number, number] | null,
    isHistorique: true,
    date: h.date,
    step: i + 1 - historique.length,
    ca_reel: h.ca_reel,
    ca_predit: null as number | null,
    borne_basse: null as number | null,
    borne_haute: null as number | null,
  }));

  const chartData = [
    ...histChartData,
    ...(result?.previsions ?? []).map((p, i) => ({
      name: `S${p.step} ${fmtDate(p.date)}`,
      ca_value: p.ca_predit,
      errorBar: [p.ca_predit - p.borne_basse, p.borne_haute - p.ca_predit] as [
        number,
        number,
      ],
      isHistorique: false,
      date: p.date,
      step: p.step,
      ca_reel: null as number | null,
      ca_predit: p.ca_predit,
      borne_basse: p.borne_basse,
      borne_haute: p.borne_haute,
    })),
  ];
  // ── Rendu ──────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: "calc(100vh - 62px)",
        background: NW.bg,
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        padding: "22px 24px",
      }}
    >
      <style>{`
        @keyframes nw-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .nw-select {
          padding: 8px 12px; font-size: 13px; font-family: Inter, system-ui, -apple-system, sans-serif;
          border: 1.5px solid ${NW.violetBorder}; border-radius: 10px;
          background: #FFFFFF; color: ${NW.text}; outline: none; cursor: pointer;
          transition: border-color 0.15s; min-width: 180px;
        }
        .nw-select:focus { border-color: ${NW.violet}; box-shadow: 0 0 0 3px rgba(29,91,168,0.12); }
        .nw-select:disabled { opacity: 0.5; cursor: not-allowed; }
        .nw-hz-btn {
          width: 44px; height: 36px; border-radius: 10px; border: 1.5px solid ${NW.violetBorder};
          background: transparent; cursor: pointer; font-size: 13px; font-weight: 700;
          color: ${NW.text}; transition: all 0.15s; font-family: Inter, system-ui, -apple-system, sans-serif;
        }
        .nw-hz-btn:hover { border-color: ${NW.violet}; color: ${NW.violet}; background: ${NW.violetLight}; }
        .nw-hz-btn.active { background: ${NW.violet}; color: #FFFFFF; border-color: ${NW.violet}; }
        .nw-btn-primary {
          padding: 10px 24px; border-radius: 12px; border: none;
          background: ${NW.violet}; color: #FFFFFF; font-family: Inter, system-ui, -apple-system, sans-serif;
          font-weight: 800; font-size: 14px; cursor: pointer; transition: background 0.15s;
          display: flex; align-items: center; gap: 8px;
        }
        .nw-btn-primary:hover:not(:disabled) { background: ${NW.violetDark}; }
        .nw-btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }
        .nw-table-row:hover { background: ${NW.violetLight} !important; }
      `}</style>

      {/* ── En-tête ─────────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 22,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            flexShrink: 0,
            background: "linear-gradient(135deg,#184B8C,#1D5BA8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 24,
            boxShadow: "0 4px 18px rgba(29,91,168,0.35)",
          }}
        >
          🔮
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontFamily: "Poppins, Inter, system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 22,
                color: NW.text,
                letterSpacing: "-0.4px",
              }}
            >
              Prévision par Horizon
            </h1>
            <div style={{ fontSize: 12, color: NW.textMuted, marginTop: 2 }}>
              Sélectionnez une Famille · Sous-famille · Dépôt et un horizon de 1
              à 11 semaines
            </div>
          </div>

          {/* ── Boutons droite ───────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onSwitchToFamille}
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                border: `1.5px solid ${NW.violetBorder}`,
                background: "#FFFFFF",
                color: NW.violet,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              📦 Famille
            </button>
            <button
              onClick={onSwitchToSousFamille}
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                border: `1.5px solid ${NW.violetBorder}`,
                background: "#FFFFFF",
                color: NW.violet,
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              🗂 Sous-famille
            </button>
            <button
              style={{
                padding: "8px 16px",
                borderRadius: 10,
                border: "none",
                background: NW.violet,
                color: "#FFFFFF",
                fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              🔍 Filtre
            </button>
          </div>
        </div>
      </div>

      {/* ── Panneau de configuration ─────────────────────────────────────────── */}
      <div
        style={{
          background: NW.card,
          borderRadius: 18,
          border: `1.5px solid ${NW.violetBorder}`,
          boxShadow: NW.shadow,
          padding: "20px 24px",
          marginBottom: 20,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* ── Ligne 1 : Famille · Sous-famille ── */}
        <div
          style={{
            display: "flex",
            gap: 20,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          {/* Famille */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              flex: 1,
              minWidth: 0,
            }}
          >
            <label
              style={{
                fontSize: 10,
                fontWeight: 800,
                color: NW.textMuted,
                textTransform: "uppercase",
                letterSpacing: 1.1,
              }}
            >
              📦 Famille
            </label>
            {loadingFamilles ? (
  <div style={{ display: "flex", alignItems: "center", gap: 8, color: NW.textMuted, fontSize: 13 }}>
    <Spinner size={14} /> Chargement des familles…
  </div>
) : famillesError ? (
  <div style={{ color: NW.red, fontSize: 12, padding: "6px 0" }}>
    ⚠️ {famillesError}
  </div>
) : (
  <select
    className="nw-select"
    style={{ width: "100%" }}
    value={famille}
    onChange={(e) => setFamille(e.target.value)}
  >
    <option value="">— Choisir —</option>
    {familles.map((f) => (
      <option key={f.codFamp} value={f.codFamp}>
        {f.codFamp} — {f.designation}
      </option>
    ))}
  </select>
)}
          </div>

          {/* Sous-famille */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              flex: 1,
              minWidth: 0,
            }}
          >
            <label
              style={{
                fontSize: 10,
                fontWeight: 800,
                color: NW.textMuted,
                textTransform: "uppercase",
                letterSpacing: 1.1,
              }}
            >
              🗂 Sous-famille
            </label>
            <select
              className="nw-select"
              style={{ width: "100%" }}
              value={sousFamille}
              onChange={(e) => setSousFamille(e.target.value)}
              disabled={!famille}
            >
              <option value="">— Choisir —</option>
              {sousFamilles.map((sf) => (
                <option key={sf.codFam} value={sf.codFam}>
                  {sf.codFam} — {sf.desgFam}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ── Ligne 2 : Sélection Dépôt (liste de cards) ── */}
        {sousFamille && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between", flexWrap: "wrap" }}>
              <label style={{ fontSize: 10, fontWeight: 800, color: NW.textMuted, textTransform: "uppercase", letterSpacing: 1.1, display: "flex", alignItems: "center", gap: 6 }}>
                🏭 Dépôt
                {loadingDepots && <Spinner size={10} />}
                {!loadingDepots && depots.length > 0 && (
                  <span style={{ fontSize: 9, fontWeight: 600, background: NW.violetLight, color: NW.violet, borderRadius: 99, padding: "1px 8px" }}>
                    {depots.length} disponible{depots.length > 1 ? "s" : ""}
                  </span>
                )}
              </label>
              {/* Recherche dépôt */}
              {depots.length > 4 && (
                <input
                  type="text"
                  placeholder="🔍 Rechercher un dépôt…"
                  value={depotSearch}
                  onChange={(e) => setDepotSearch(e.target.value)}
                  style={{
                    padding: "6px 12px",
                    fontSize: 12,
                    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                    border: `1.5px solid ${NW.violetBorder}`,
                    borderRadius: 8,
                    outline: "none",
                    background: "#FFFFFF",
                    color: NW.text,
                    minWidth: 200,
                  }}
                />
              )}
            </div>

            {loadingDepots ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: NW.textMuted, fontSize: 13, padding: "12px 0" }}>
                <Spinner size={14} /> Chargement des dépôts…
              </div>
            ) : depots.length === 0 ? (
              <div style={{ fontSize: 12, color: NW.textMuted, fontStyle: "italic", padding: "10px 0" }}>
                Aucun dépôt disponible pour cette sélection.
              </div>
            ) : (
              <div style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                maxHeight: 180,
                overflowY: "auto",
                padding: "4px 0",
              }}>
                {depots
                  .filter((d) =>
                    !depotSearch ||
                    d.codDep.toLowerCase().includes(depotSearch.toLowerCase()) ||
                    d.libelle.toLowerCase().includes(depotSearch.toLowerCase())
                  )
                  .map((d) => {
                    const isActive = depot === d.codDep;
                    return (
                      <button
                        key={d.codDep}
                        onClick={() => setDepot(isActive ? "" : d.codDep)}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 2,
                          padding: "8px 14px",
                          borderRadius: 10,
                          border: `1.5px solid ${isActive ? NW.violet : NW.violetBorder}`,
                          background: isActive ? NW.violet : "#FFFFFF",
                          color: isActive ? "#FFFFFF" : NW.text,
                          cursor: "pointer",
                          transition: "all 0.15s",
                          boxShadow: isActive ? `0 4px 14px rgba(29,91,168,0.25)` : "none",
                          fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                          minWidth: 130,
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            (e.currentTarget as HTMLElement).style.borderColor = NW.violet;
                            (e.currentTarget as HTMLElement).style.background = NW.violetLight;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            (e.currentTarget as HTMLElement).style.borderColor = NW.violetBorder;
                            (e.currentTarget as HTMLElement).style.background = "#FFFFFF";
                          }
                        }}
                      >
                        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4 }}>
                          {d.codDep}
                        </span>
                        {d.libelle && d.libelle !== d.codDep && (
                          <span style={{ fontSize: 10, fontWeight: 600, opacity: isActive ? 0.85 : 0.6, lineHeight: 1.2 }}>
                            {d.libelle}
                          </span>
                        )}
                        {isActive && (
                          <span style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>✓ Sélectionné</span>
                        )}
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
        )}

        {/* ── Ligne 3 : Vendeur (optionnel) ── */}
        {depot && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 10, fontWeight: 800, color: NW.textMuted, textTransform: "uppercase", letterSpacing: 1.1, display: "flex", alignItems: "center", gap: 6 }}>
              👤 Vendeur
              {loadingVendeurs && <Spinner size={10} />}
              <span style={{ fontSize: 9, fontWeight: 600, color: NW.textMuted, textTransform: "none" }}>(optionnel)</span>
            </label>
            <select
              className="nw-select"
              style={{ maxWidth: 320 }}
              value={vendeur}
              onChange={(e) => setVendeur(e.target.value)}
              disabled={loadingVendeurs}
            >
              <option value="">{loadingVendeurs ? "Chargement…" : "— Tous les vendeurs —"}</option>
              {vendeurs.map((v) => (
                <option key={v.codeVendeur} value={v.codeVendeur}>
                  {v.codeVendeur}{v.libelle && v.libelle !== v.codeVendeur ? ` — ${v.libelle}` : ""}
                </option>
              ))}
            </select>
            {!loadingVendeurs && vendeurs.length > 0 && (
              <div style={{ fontSize: 10, color: NW.textMuted, fontWeight: 600 }}>
                {vendeurs.length} vendeur{vendeurs.length > 1 ? "s" : ""} disponible{vendeurs.length > 1 ? "s" : ""}
              </div>
            )}
          </div>
        )}

        {/* ── Séparateur horizontal ── */}
        <div style={{ height: 1, background: NW.border }} />

        {/* ── Ligne 2 : Horizon + Bouton ── */}
        <div style={{ display: "flex", gap: 20, alignItems: "flex-end" }}>
          {/* Horizon */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              flex: 1,
            }}
          >
            <label
              style={{
                fontSize: 10,
                fontWeight: 800,
                color: NW.textMuted,
                textTransform: "uppercase",
                letterSpacing: 1.1,
              }}
            >
              📅 Horizon (semaines)
            </label>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {HORIZONS.map((h) => (
                <button
                  key={h}
                  className={`nw-hz-btn${horizon === h ? " active" : ""}`}
                  onClick={() => setHorizon(h)}
                >
                  {h}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 10, color: NW.textMuted, fontWeight: 600 }}>
              Horizon sélectionné :{" "}
              <strong style={{ color: NW.violet }}>
                {horizon} semaine{horizon > 1 ? "s" : ""}
              </strong>
            </div>
          </div>

          {/* Bouton Lancer */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              alignItems: "flex-end",
            }}
          >
            <button
              className="nw-btn-primary"
              disabled={!canForecast || loading}
              onClick={handleForecast}
            >
              {loading ? (
                <>
                  <Spinner size={14} />
                  <span>Calcul en cours…</span>
                </>
              ) : (
                "⚡ Lancer la prévision"
              )}
            </button>
            {!canForecast && (
              <div
                style={{ fontSize: 10, color: NW.textMuted, fontWeight: 600 }}
              >
                ← Sélectionnez famille, sous-famille et dépôt
              </div>
            )}
          </div>
        </div>
      </div>
      

      {/* ── Preview automatique sous-famille (avant choix dépôt) ────────────── */}
      {canAutoPreview && !depot && (autoLoading || autoResult) && (
        <div style={{
          background: NW.card,
          borderRadius: 16,
          border: `1.5px solid ${NW.violetBorder}`,
          boxShadow: NW.shadow,
          padding: "18px 22px",
          marginBottom: 18,
        }}>
          <div style={{
            fontFamily: "Poppins, Inter, system-ui, sans-serif",
            fontWeight: 800,
            fontSize: 13,
            color: NW.textMuted,
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}>
            {autoLoading ? (
              <><Spinner size={13} /> Calcul aperçu pour <strong style={{ color: NW.violet }}>{sousFamille}</strong>…</>
            ) : (
              <>📊 Aperçu prévision — <strong style={{ color: NW.violet }}>{sousFamille}</strong>
              <span style={{ fontSize: 11, fontWeight: 600, color: NW.textMuted }}>
                &nbsp;(dépôt : {autoResult?.depot})
              </span></>
            )}
          </div>

          {autoResult?.previsions && !autoLoading && (
            <>
              {/* KPIs rapides */}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
                <div style={{
                  background: NW.violetLight,
                  borderRadius: 12,
                  padding: "10px 18px",
                  flex: 1, minWidth: 140,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: NW.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                    CA PRÉDIT TOTAL
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: NW.violet }}>
                    {fmtCa(autoResult.ca_total)}
                  </div>
                </div>
                <div style={{
                  background: "#F8FAFC",
                  borderRadius: 12,
                  padding: "10px 18px",
                  flex: 1, minWidth: 140,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#006357", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                    PÉRIODE
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#00806F" }}>
                    {fmtDate(autoResult.date_from ?? "")} → {fmtDate(autoResult.date_to ?? "")}
                  </div>
                </div>
                <div style={{
                  background: "#F8FAFC",
                  borderRadius: 12,
                  padding: "10px 18px",
                  flex: 1, minWidth: 140,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#8A5A02", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                    MEILLEURE SEMAINE
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#B83838" }}>
                    {fmtCa(Math.max(...autoResult.previsions.map(p => p.ca_predit)))}
                  </div>
                </div>
              </div>

              {/* Mini graphique */}
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={autoResult.previsions} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={NW.violetBorder} />
                  <XAxis dataKey="step" tickFormatter={(v) => `S${v}`}
                    tick={{ fontSize: 10, fill: NW.textMuted }} />
                  <YAxis tick={{ fontSize: 10, fill: NW.textMuted }}
                    tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : String(v)} />
                  <Tooltip formatter={(v: any) => [fmtCa(v), "CA prédit"]} />
                  <Bar dataKey="ca_predit" radius={[5, 5, 0, 0]} maxBarSize={40}>
                    {autoResult.previsions.map((_, i) => (
                      <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <div style={{ fontSize: 11, color: NW.textMuted, marginTop: 8, fontStyle: "italic" }}>
                💡 Sélectionnez un dépôt et lancez la prévision pour un résultat précis.
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Erreur ──────────────────────────────────────────────────────────── */}
      {errMsg && (() => {
  // Tenter de parser le JSON brut retourné par Flask
  let msg = errMsg;
  try {
    const parsed = JSON.parse(msg);
    if (parsed?.error) msg = parsed.error;
  } catch {}

  const isInsuffisant =
    msg.toLowerCase().includes("introuvable") ||
    msg.toLowerCase().includes("insuffisant") ||
    msg.toLowerCase().includes("absente") ||
    msg.toLowerCase().includes("aucune prévision") ||
    msg.toLowerCase().includes("aucune prevision") ||
    msg.toLowerCase().includes("filtr");

  if (isInsuffisant) {
    return (
      <div style={{
        background: "#FEF6E6",
        border: "1.5px solid #D98E0444",
        borderRadius: 16,
        padding: "24px 28px",
        marginBottom: 18,
        display: "flex",
        gap: 18,
        alignItems: "flex-start",
      }}>
        <div style={{ fontSize: 36, flexShrink: 0 }}>📊</div>
        <div>
          <div style={{
            fontFamily: "Poppins, Inter, system-ui, sans-serif",
            fontWeight: 800,
            fontSize: 15,
            color: "#8A5A02",
            marginBottom: 6,
          }}>
            Données insuffisantes
          </div>
          <div style={{ fontSize: 13, color: "#B07303", fontWeight: 600, marginBottom: 10 }}>
            La combinaison sélectionnée n'a pas assez d'historique pour générer une prévision.
          </div>
          <div style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
          }}>
            {[
              { label: "Famille",      val: famille },
              { label: "Sous-famille", val: sousFamille },
              { label: "Dépôt",        val: depot },
            ].map((item) => (
              <span key={item.label} style={{
                background: "#FCE7BF",
                color: "#8A5A02",
                borderRadius: 8,
                padding: "3px 10px",
                fontSize: 12,
                fontWeight: 700,
                border: "1px solid #D98E0433",
              }}>
                {item.label} : <strong>{item.val}</strong>
              </span>
            ))}
          </div>
          <div style={{
            marginTop: 12,
            fontSize: 12,
            color: "#B07303",
            fontStyle: "italic",
          }}>
            💡 Essayez une autre combinaison famille / sous-famille / dépôt.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: NW.redBg,
      color: NW.red,
      borderRadius: 14,
      padding: "14px 18px",
      fontWeight: 700,
      fontSize: 13,
      marginBottom: 18,
      border: `1px solid ${NW.red}22`,
      display: "flex",
      alignItems: "center",
      gap: 10,
    }}>
      <span style={{ fontSize: 18 }}>⚠️</span>
      <span>{msg}</span>
    </div>
  );
})()}

      {/* ── Résultats ───────────────────────────────────────────────────────── */}
      {result && result.success && result.previsions && (
        <>
          {/* ── Résumé ──────────────────────────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 20,
            }}
          >
            {/* CA total */}
            <div
              style={{
                background: NW.card,
                borderRadius: 14,
                padding: "14px 20px",
                border: `2px solid ${NW.violet}33`,
                boxShadow: NW.shadow,
                flex: 1,
                minWidth: 160,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: NW.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 4,
                }}
              >
                CA PRÉDIT TOTAL
              </div>
              <div style={{ fontSize: 24, fontWeight: 900, color: NW.violet }}>
                {fmtCa(result.ca_total)}
              </div>
            </div>

            {/* Période */}
            <div
              style={{
                background: NW.card,
                borderRadius: 14,
                padding: "14px 20px",
                border: `1.5px solid ${NW.violetBorder}`,
                boxShadow: NW.shadow,
                flex: 1,
                minWidth: 180,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: NW.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 4,
                }}
              >
                PÉRIODE
              </div>
              <div style={{ fontSize: 14, fontWeight: 800, color: NW.text }}>
                {fmtDate(result.date_from ?? "")} →{" "}
                {fmtDate(result.date_to ?? "")}
              </div>
            </div>

            {/* Granularité */}
            <div
              style={{
                background: NW.card,
                borderRadius: 14,
                padding: "14px 20px",
                border: `1.5px solid ${NW.violetBorder}`,
                boxShadow: NW.shadow,
                flex: 2,
                minWidth: 220,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: NW.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginBottom: 4,
                }}
              >
                GRANULARITÉ
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { label: "Famille", val: result.famille },
                  { label: "Sous-famille", val: result.sous_famille },
                  { label: "Dépôt", val: result.depot },
                  ...(vendeur ? [{ label: "Vendeur", val: vendeur }] : []),
                  { label: "Horizon", val: `${result.horizon} sem.` },
                ].map((item) => (
                  <span
                    key={item.label}
                    style={{
                      background: NW.violetLight,
                      color: NW.violet,
                      borderRadius: 8,
                      padding: "3px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {item.label} : <strong>{item.val}</strong>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* ── Graphique ───────────────────────────────────────────────────── */}
          <div
            style={{
              background: NW.card,
              borderRadius: 18,
              border: `1.5px solid ${NW.violetBorder}`,
              boxShadow: NW.shadow,
              padding: "20px 16px",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                fontFamily: "Poppins, Inter, system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 14,
                color: NW.text,
                marginBottom: 16,
                paddingLeft: 8,
              }}
            >
              📊 Prévision semaine par semaine — {result.famille} /{" "}
              {result.sous_famille} / {result.depot}
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 20, bottom: 20, left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={NW.violetBorder} />
                <XAxis
                  dataKey="name"
                  tick={{
                    fontSize: 11,
                    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                    fill: NW.textMuted,
                  }}
                  interval={0}
                />
                <YAxis
                  tick={{
                    fontSize: 11,
                    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
                    fill: NW.textMuted,
                  }}
                  tickFormatter={(v) =>
                    v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)
                  }
                />
                <Tooltip content={<CustomTooltipCombined />} />

                {/* Séparateur visuel entre historique et prévision */}
                {historique.length > 0 && (
                  <ReferenceLine
                    x={`S1\n${fmtDate(result?.previsions?.[0]?.date ?? "")}`}
                    stroke={NW.violet}
                    strokeDasharray="4 3"
                    strokeWidth={1.5}
                    label={{
                      value: "Aujourd'hui",
                      position: "insideTopLeft",
                      fontSize: 10,
                      fill: NW.violet,
                    }}
                  />
                )}

                {/* Une seule Bar — couleur selon isHistorique */}
                <Bar
                  dataKey="ca_value"
                  radius={[6, 6, 0, 0]}
                  maxBarSize={52}
                  name="CA"
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`bar-${index}`}
                      fill={
                        entry.isHistorique
                          ? "#00A08C"
                          : BAR_COLORS[index % BAR_COLORS.length]
                      }
                      fillOpacity={entry.isHistorique ? 0.85 : 1}
                    />
                  ))}
                  <ErrorBar
                    dataKey="errorBar"
                    width={4}
                    strokeWidth={2}
                    stroke={NW.textMuted}
                    direction="y"
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Légende couleurs */}
            <div
              style={{
                display: "flex",
                gap: 18,
                justifyContent: "center",
                marginTop: 8,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: "#00A08C",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background: "#00A08C",
                  }}
                />
                CA Réel (10 sem. passées)
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: NW.violet,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background: NW.violet,
                  }}
                />
                Prévision
              </span>
            </div>
            <div
              style={{
                fontSize: 11,
                color: NW.textMuted,
                textAlign: "center",
                marginTop: 6,
              }}
            >
              Les barres verticales représentent les intervalles de confiance
              (±15 % minimum, élargis avec l'horizon)
            </div>
          </div>

          {/* ── Tableau détaillé ─────────────────────────────────────────────── */}
          <div
            style={{
              background: NW.card,
              borderRadius: 18,
              border: `1.5px solid ${NW.violetBorder}`,
              boxShadow: NW.shadow,
              overflow: "hidden",
              marginBottom: 20,
            }}
          >
            <div
              style={{
                fontFamily: "Poppins, Inter, system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 14,
                color: NW.text,
                padding: "16px 20px 0",
              }}
            >
              📋 Tableau des prévisions
            </div>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: `linear-gradient(135deg, rgba(29,91,168,0.06), rgba(29,91,168,0.04))`,
                      borderBottom: `2px solid ${NW.violetBorder}`,
                    }}
                  >
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "center",
                        color: NW.textMuted,
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      Étape
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        color: NW.textMuted,
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      Date semaine
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                        color: NW.textMuted,
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      Borne basse
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                        color: NW.violet,
                        fontWeight: 800,
                        fontSize: 12,
                        background: "rgba(29,91,168,0.05)",
                        borderLeft: `3px solid ${NW.violet}33`,
                      }}
                    >
                      🔮 CA Prédit
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                        color: NW.textMuted,
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      Borne haute
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                        color: NW.textMuted,
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      Intervalle
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {result.previsions.map((p, i) => {
                    const delta = p.borne_haute - p.borne_basse;
                    return (
                      <tr
                        key={p.step}
                        className="nw-table-row"
                        style={{
                          background:
                            i % 2 === 0
                              ? "transparent"
                              : "rgba(29,91,168,0.018)",
                          borderBottom: `1px solid ${NW.border}`,
                          transition: "background 0.12s",
                        }}
                      >
                        <td
                          style={{ padding: "10px 16px", textAlign: "center" }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              background:
                                BAR_COLORS[i % BAR_COLORS.length] + "22",
                              color: BAR_COLORS[i % BAR_COLORS.length],
                              fontWeight: 800,
                              fontSize: 12,
                              lineHeight: "28px",
                              textAlign: "center",
                            }}
                          >
                            {p.step}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            fontWeight: 700,
                            color: NW.text,
                          }}
                        >
                          {fmtDate(p.date)}
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "right",
                            color: NW.textMuted,
                            fontWeight: 600,
                            fontSize: 12,
                          }}
                        >
                          {fmtCa(p.borne_basse)}
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "right",
                            background: "rgba(29,91,168,0.05)",
                            borderLeft: `3px solid ${NW.violet}22`,
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 900,
                              fontSize: 15,
                              color: NW.violet,
                            }}
                          >
                            {fmtCa(p.ca_predit)}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "right",
                            color: NW.textMuted,
                            fontWeight: 600,
                            fontSize: 12,
                          }}
                        >
                          {fmtCa(p.borne_haute)}
                        </td>
                        <td
                          style={{
                            padding: "10px 16px",
                            textAlign: "right",
                            color: NW.textMuted,
                            fontSize: 11,
                            fontStyle: "italic",
                          }}
                        >
                          ±{fmtCa(delta / 2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr
                    style={{
                      background: NW.violetLight,
                      borderTop: `2px solid ${NW.violetBorder}`,
                    }}
                  >
                    <td
                      colSpan={3}
                      style={{
                        padding: "12px 16px",
                        fontWeight: 800,
                        fontSize: 13,
                        color: NW.violet,
                      }}
                    >
                      TOTAL — {result.horizon} semaines
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                        background: "rgba(29,91,168,0.08)",
                        borderLeft: `3px solid ${NW.violet}33`,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 900,
                          fontSize: 17,
                          color: NW.violet,
                        }}
                      >
                        {fmtCa(result.ca_total)}
                      </div>
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* ── Légende ─────────────────────────────────────────────────────── */}
          <div
            style={{
              fontSize: 11,
              color: NW.textMuted,
              display: "flex",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            <span>📅 Chaque ligne = une semaine (lundi)</span>
            <span>
              🔵 Borne basse/haute = intervalle de confiance (±15 % min, élargit
              avec l'horizon)
            </span>
            <span>🔮 CA prédit = valeur centrale du modèle LightGBM</span>
          </div>
        </>
      )}

      {/* ── État vide ────────────────────────────────────────────────────────── */}
      {!result && !loading && !errMsg && (
        <div
          style={{
            background: NW.card,
            borderRadius: 18,
            border: `1.5px dashed ${NW.violetBorder}`,
            padding: "60px 40px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔮</div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: NW.text,
              marginBottom: 6,
            }}
          >
            Prêt à prévoir
          </div>
          <div style={{ fontSize: 13, color: NW.textMuted }}>
            Sélectionnez une famille, une sous-famille, un dépôt et choisissez
            votre horizon (1 → 11 semaines)
          </div>
        </div>
      )}
    </div>
  );
};

export default PrevisionFView;
